/**
 * The navigation model — one definition, read by the sidebar, the router guard and
 * the landing-route resolver.
 *
 * docs/CLINIC_MULTI_CLINIC_UX_REV2.md §5.8. Three rules it encodes:
 *
 * 1. AN ENTRY THAT LEADS TO A DENIAL IS WORSE THAN NO ENTRY. The sidebar FILTERS on
 *    permission; it never renders a disabled row. A warehouse keeper who can see
 *    «صندوق» greyed out learns only that the product is withholding something.
 * 2. THE LANDING ROUTE IS DERIVED, never hardcoded to `/reception`. A warehouse
 *    keeper whose only grant is `inventory.view` must land on «کالاها و خدمات»;
 *    sending them to a reception page that 403s is a broken front door.
 * 3. THE SIDEBAR AND THE BOUNDARY READ THE SAME SOURCE — these keys are catalog
 *    keys from the BFF's permissionCatalog.js, and the permissions themselves are
 *    resolved server-side per clinic. The client cannot disagree with the guard
 *    because it is not making an independent judgement.
 *
 * Labels stay in ClinicLayout with the icons: this module is the POLICY, and
 * keeping copy out of it means a permission change never touches a translation.
 */

/** Nav entries in sidebar order. `perm` may be a key, an array (any-of), or null. */
export const NAV_PERMISSIONS = [
  { id: 'reception', perm: 'appointments.view' },
  { id: 'patients', perm: 'patients.view' },
  // «کالاها و خدمات» — the warehouse, the service declarations and the equipment
  // register. FOUR keys, any one of which opens it, because four different jobs
  // land here: stock (مسئول انبار), the price list (مدیر), the device register,
  // and the insurer contracts. Two of those four — `equipment.view` and
  // `contracts.manage` — opened NO sidebar row before the split even though the
  // rail inside had an entry for each, so a person holding only one of them was
  // denied at the gate for a screen built for them.
  { id: 'goods', perm: ['inventory.view', 'services.view', 'equipment.view', 'contracts.manage'] },
  // «همکاران» — the clinic's people, their roles, and since 2026-09-13 their
  // payroll. THREE keys, any one of which opens the row, because the page is now
  // three objects behind one population: the roster (`people.view`), the
  // practitioner half (`doctors.view` — which opened no row of its own before,
  // so a practitioner manager was denied at the gate for a screen built for
  // them), and the monthly run (`payroll.view`).
  //
  // Payroll is still its OWN key and not part of `people.*`: a منشی holds
  // `people.view` and must not see what everyone earns, so the TAB is absent —
  // not disabled, absent — without it. What changed is only that a salary screen
  // is not a second row in the sidebar for the same people.
  { id: 'people', perm: ['people.view', 'doctors.view', 'payroll.view'] },
  // «مدیریت هزینه‌ها» — the expense ledger, قبوض and the definitions that classify
  // them. It was «عملیات و هزینه» until 2026-09-12, when the warehouse and the
  // service list moved to their own page
  // (docs/CLINIC_EXPENSES_AND_GOODS_SPLIT_DESIGN.md), and its gate narrowed with
  // it: what remains here is money out, and `expenses.view` is the key for that.
  { id: 'operations', perm: 'expenses.view' },
  // «حسابداری» — the Financial Operations hub, in the slot «پرداخت‌ها» used to
  // hold (docs/CLINIC_ACCOUNTING_FINOPS_DESIGN.md §7.2, D3). EITHER key opens it:
  // the receipts ledger and the claims ledger MOVED INTO it, so a person who
  // could open the old row on `payments.view` alone must still reach them — and
  // an accountant holding `accounting.view` reaches the rest. The rail inside
  // then shows each of them only what their own keys permit.
  { id: 'accounting', perm: ['accounting.view', 'payments.view'] },
  // EITHER reports key opens the module. An accountant may hold only
  // `reports.finance.view` — the P&L is their job and the visit counts are not — and
  // a row gated on `reports.view` alone would hide the screen from the person who
  // reads it most. `allows` treats an array as "any of", which is the right reading
  // here; the RAIL inside then shows each of them only what their own keys permit.
  { id: 'reports', perm: ['reports.view', 'reports.finance.view'] },
  // «فروشگاه» — what the clinic SELLS to the public, and what people bought. It
  // is the commercial continuation of «کالاها و خدمات»:
  // a listing is always created FROM an item or a service
  // declared there, never invented here (docs/CLINIC_STORE_MARKETPLACE_DESIGN.md
  // §3, §8.1). ONE key opens the row; the rail inside then gates finance and
  // settings on their own keys.
  { id: 'store', perm: 'store.view' },
  { id: 'clinic', perm: ['clinic.settings.manage', 'clinic.billing.manage'] },
  // «کارتابل خدمات» (PM-0693), after the clinic's own 2026-09-30 order rather than
  // inside it: the person's own booked services. The list is
  // scoped to the caller by the BFF; the row shows for whoever records services.
  { id: 'myServices', perm: 'orders.complete' },
];

/**
 * Person-scoped rows: shown on a property of the PERSON, not of their permissions.
 *
 * «دعوت‌ها» is the only one, and it is deliberately not a permanent row — it appears
 * when there is an invitation waiting and disappears when there is not. A permanent
 * «دعوت‌ها (۰)» is the sort of standing nag that trains people to stop reading the
 * sidebar, which then fails when something real needs attention.
 */
export const PERSON_NAV = [
  { id: 'invitations', when: (ctx) => (ctx.pendingInvitations?.length || 0) > 0 },
];

/** Route path (without the `/c/:ref` prefix) → the permission needed to open it. */
export const ROUTE_PERMISSIONS = {
  '/reception': 'appointments.view',
  '/patients': 'patients.view',
  '/patients/new': 'patients.manage',
  '/operations': 'expenses.view',
  '/goods': ['inventory.view', 'services.view', 'equipment.view', 'contracts.manage'],
  '/store': 'store.view',
  '/reports': ['reports.view', 'reports.finance.view'],
  '/accounting': ['accounting.view', 'payments.view'],
  '/people': ['people.view', 'doctors.view', 'payroll.view'],
  '/people/roles': 'roles.manage',
  '/clinic': ['clinic.settings.manage', 'clinic.billing.manage'],
};

/** Does `access` satisfy a `perm` spec (null / key / array-of-keys)? */
export function allows(access, perm) {
  if (!perm) return true;
  if (Array.isArray(perm)) return access.canAny(perm);
  return access.can(perm);
}

/** The nav ids this person may open, in sidebar order. */
export function visibleNavIds(access) {
  if (!access?.ready) return [];
  return NAV_PERMISSIONS.filter((n) => allows(access, n.perm)).map((n) => n.id);
}

/**
 * Longest-prefix match, so `/people/roles/abc` is judged by `/people/roles` rather
 * than by `/people` — a viewer who may read the roster but not edit roles must be
 * stopped at the role editor, and the shorter prefix would wave them through.
 */
export function permissionForPath(path) {
  const clean = (path || '').split('?')[0].replace(/\/+$/, '') || '/';
  let best = null;
  for (const [prefix, perm] of Object.entries(ROUTE_PERMISSIONS)) {
    if (clean === prefix || clean.startsWith(`${prefix}/`)) {
      if (!best || prefix.length > best.prefix.length) best = { prefix, perm };
    }
  }
  return best ? best.perm : null;
}

export function mayOpenPath(access, path) {
  return allows(access, permissionForPath(path));
}

/** Where a bare `/c/:ref/` goes: the first entry this person can actually open. */
export const NAV_LANDING = {
  reception: '/reception',
  myServices: '/my-services',
  accounting: '/accounting',
  operations: '/operations',
  goods: '/goods',
  store: '/store',
  patients: '/patients',
  reports: '/reports',
  people: '/people',
  clinic: '/clinic',
};

export function landingPath(access) {
  const [first] = visibleNavIds(access);
  // No first entry means no permissions at all — a real state (§5.9.2) that gets
  // its own page rather than a redirect loop through a route they cannot open.
  return first ? NAV_LANDING[first] : null;
}
